import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { StockCategoryFilter } from '../types/stockCategories';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';

export interface LeaderboardEntry {
  rank: number;
  username: string;
  guesses: number;
  time_ms: number;
  is_current_user: boolean;
  percentile: string; // "23.50" or "?"
  total_players?: number;
  won: boolean; // true = successful completion, false = DNF (Did Not Finish)
  equipped_achievement_icon_url?: string | null;
}

interface UseLeaderboardOptions {
  category: StockCategoryFilter;
  gameDate?: string;
  userId?: string | null;
  deviceId?: string | null;
  autoFetch?: boolean;
}

export const useLeaderboard = ({
  category,
  gameDate,
  userId = null,
  deviceId = null,
  autoFetch = true
}: UseLeaderboardOptions) => {
  const queryClient = useQueryClient();
  const effectiveGameDate = gameDate || getCurrentGameTimestamp();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leaderboard', category, effectiveGameDate, userId || deviceId],
    queryFn: async () => {
      console.log('🔍 [useLeaderboard] Fetching leaderboard:', {
        category,
        effectiveGameDate,
        userId,
        deviceId
      });

      const { data, error: rpcError } = await supabase.rpc('get_category_leaderboard', {
        p_category: category,
        p_game_date: effectiveGameDate,
        p_user_id: userId,
        p_device_id: deviceId
      });

      if (rpcError) {
        console.error('🔴 [useLeaderboard] RPC Error:', rpcError);
        throw rpcError;
      }

      console.log('✅ [useLeaderboard] RPC Success:', {
        dataLength: data?.length || 0,
        rawData: data,
        firstEntry: data?.[0]
      });

      return (data || []) as LeaderboardEntry[];
    },
    staleTime: 3 * 60 * 1000, // 3 minutes - data is fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache (formerly cacheTime)
    enabled: autoFetch,
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: autoFetch ? 'always' : false, // Ensure a fresh read whenever UI opens
  });

  // Parse the data into top5 and currentUserRank
  const top5 = data?.filter((e) => e.rank <= 5) || [];
  const userEntry = data?.find((e) => e.is_current_user) || null;
  const currentUserRank = userEntry && userEntry.rank > 5 ? userEntry : null;

  // Also expose currentUserEntry for percentile display (includes top 5 users)
  const currentUserEntry = userEntry;

  console.log('📊 [useLeaderboard] Parsed Results:', {
    autoFetch,
    isLoading,
    hasError: !!error,
    errorMessage: error instanceof Error ? error.message : null,
    top5Count: top5.length,
    hasUserEntry: !!userEntry,
    currentUserEntry: currentUserEntry ? {
      rank: currentUserEntry.rank,
      username: currentUserEntry.username,
      percentile: currentUserEntry.percentile,
      total_players: currentUserEntry.total_players,
      is_current_user: currentUserEntry.is_current_user
    } : null,
    currentUserRank: currentUserRank ? {
      rank: currentUserRank.rank,
      percentile: currentUserRank.percentile
    } : null
  });

  // Helper to invalidate this leaderboard's cache
  const invalidateLeaderboard = () => {
    queryClient.invalidateQueries({ queryKey: ['leaderboard', category, effectiveGameDate] });
  };

  return {
    top5,
    currentUserRank,
    currentUserEntry, // New: includes percentile data for current user
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetch,
    invalidateLeaderboard // New: helper to refresh cache
  };
};
